import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">{title}</h2>
    <div className="space-y-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
      {children}
    </div>
  </section>
);

const Privacy = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-2xl sm:text-3xl font-bold">Privacy Policy</CardTitle>
          <CardDescription>
            Last updated: Feb 7, 2026
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="text-sm sm:text-base text-muted-foreground leading-relaxed space-y-3">
            <p>
              This Privacy Policy describes how NAndroidEx ("we", "us") collects, uses, and shares
              information when you use IronSource Dashboard (the "Service"), including the web app and
              related mobile applications.
            </p>
            <p>
              This page is provided as a general template and will be refined as we finalize the frontend
              privacy/terms implementation and product flows.
            </p>
          </div>

          <div className="rounded-lg border bg-card p-4 sm:p-5">
            <h2 className="text-base sm:text-lg font-semibold text-foreground mb-2">Contents</h2>
            <ul className="grid gap-2 text-sm sm:text-base text-primary">
              <li><a className="hover:underline" href="#information-we-collect">Information we collect</a></li>
              <li><a className="hover:underline" href="#how-we-use-information">How we use information</a></li>
              <li><a className="hover:underline" href="#sharing">Sharing</a></li>
              <li><a className="hover:underline" href="#security">Security</a></li>
              <li><a className="hover:underline" href="#retention">Data retention</a></li>
              <li><a className="hover:underline" href="#your-choices">Your choices</a></li>
              <li><a className="hover:underline" href="#changes">Changes to this policy</a></li>
            </ul>
          </div>

          <div className="space-y-8">
            <Section id="information-we-collect" title="Information we collect">
              <p>
                We may collect information you provide directly (for example, account credentials or publisher
                identifiers), as well as usage and diagnostic information generated when you use the Service.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Account and authentication information</li>
                <li>App/publisher identifiers and configuration details</li>
                <li>Usage metrics (pages viewed, feature usage, timestamps)</li>
                <li>Device/browser information (approximate location, IP address, user agent)</li>
                <li>Error logs and performance diagnostics</li>
              </ul>
            </Section>

            <Section id="how-we-use-information" title="How we use information">
              <p>We use information to operate, maintain, and improve the Service, including to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Authenticate users and secure accounts</li>
                <li>Provide analytics dashboards and reports</li>
                <li>Monitor reliability, prevent abuse, and debug issues</li>
                <li>Improve performance, usability, and features</li>
              </ul>
            </Section>

            <Section id="sharing" title="Sharing">
              <p>
                We may share information with service providers that help us operate the Service (for example,
                hosting, analytics, and support tooling). We may also share information if required by law or
                to protect the security and integrity of the Service.
              </p>
            </Section>

            <Section id="security" title="Security">
              <p>
                We implement reasonable technical and organizational measures designed to protect information.
                However, no method of transmission or storage is completely secure.
              </p>
            </Section>

            <Section id="retention" title="Data retention">
              <p>
                We retain information for as long as necessary to provide the Service and for legitimate
                business purposes such as security, compliance, and dispute resolution.
              </p>
            </Section>

            <Section id="your-choices" title="Your choices">
              <p>
                You may be able to access, update, or delete certain information through the Service. If you
                need help with privacy requests, please contact us through the support channels provided in
                the Service.
              </p>
            </Section>

            <Section id="changes" title="Changes to this policy">
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page with
                an updated "Last updated" date.
              </p>
            </Section>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Privacy;
